import React, { useEffect, useState } from 'react';
import { Alert, Button, Dialog, DialogActions, DialogContent, DialogTitle, Typography, TextField, Grid, CircularProgress, Autocomplete, Chip, Divider } from "@mui/material";
import _ from "lodash";
import Div from "@jumbo/shared/Div";
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined';
import NetworkCheckIcon from '@mui/icons-material/NetworkCheck';
import { LoadingButton } from '@mui/lab';
import printerServices from 'app/services/printer-services';
import qzPrintServices from 'app/services/qz-print-services';

const DEFAULT_RECORD = {
    name: '', ip: '', port: 9100, kind: 'zebra', active: true,
    printer_name: '', dpi: 203, label_width_mm: 100, label_height_mm: 152.5, media_type: 'gap',
};

// Papeles habituales, para no tener que acordarse de los milímetros.
const PAPELES = [
    { label: '10 × 15 cm (troquelada)', ancho: 100, alto: 152.5 },
    { label: '10 × 10 cm', ancho: 100, alto: 100 },
    { label: '4 × 6 pulgadas', ancho: 101.6, alto: 152.4 },
    { label: '10 × 5 cm', ancho: 100, alto: 50 },
];

const CAMPOS_GUARDABLES = ['name', 'ip', 'port', 'kind', 'active', 'centre_id',
    'printer_name', 'dpi', 'label_width_mm', 'label_height_mm', 'media_type'];

const PrinterEditModal = (props) => {
    const { recordID, isOpen, onClose = () => {}, onRecordChange = () => {}, onRecordAdd = () => {} } = props;
    const [currentRecord, setCurrentRecord] = useState(null);
    const [editOptions, setEditOptions] = useState(null);
    const [loadingRecord, setLoadingRecord] = useState(false);
    const [saving, setSaving] = useState(false);
    const [toggleActivationLoading, setToggleActivationLoading] = useState(false);
    const [probando, setProbando] = useState(false);
    const [buscando, setBuscando] = useState(false);
    const [localPrinters, setLocalPrinters] = useState(null);
    const [aviso, setAviso] = useState(null); // { tipo: 'success' | 'error' | 'info', texto }

    // Pregunta a QZ Tray qué impresoras tiene instaladas el PC. Es lo más cerca
    // que se puede estar de "buscar la impresora": el navegador no puede barrer
    // la red (no abre sockets y desde https bloquea el http de la LAN), pero si
    // la Zebra está instalada en el PC, guardando el nombre se imprime por
    // nombre y la IP deja de importar aunque el DHCP la cambie.
    const buscarImpresoras = async () => {
        setBuscando(true);
        setAviso(null);
        try {
            const lista = await qzPrintServices.listLocalPrinters();
            setLocalPrinters(lista);
            setAviso(lista.length
                ? { tipo: 'info', texto: `QZ Tray ve ${lista.length} impresora(s) en este PC. Elegí la que corresponda.` }
                : { tipo: 'info', texto: 'QZ Tray no ve ninguna impresora instalada en este PC.' });
        } catch (err) {
            setAviso({ tipo: 'error', texto: `No se pudo hablar con QZ Tray: ${err?.message || err}. ¿Está corriendo?` });
        }
        setBuscando(false);
    };

    const probarConexion = async () => {
        setProbando(true);
        setAviso(null);
        try {
            const r = await qzPrintServices.probe({
                printerName: currentRecord.printer_name,
                host: currentRecord.ip,
                port: currentRecord.port,
            });
            setAviso({ tipo: 'success', texto: `Responde (${r.via}). No se imprimió nada: fue una consulta de estado.` });
        } catch (err) {
            setAviso({ tipo: 'error', texto: `No responde: ${err?.message || err}` });
        }
        setProbando(false);
    };

    useEffect(() => {
        if (!isOpen) return;
        if (recordID) {
            loadRecord(recordID);
        } else {
            setCurrentRecord({ ...DEFAULT_RECORD });
        }
        if (!editOptions) {
            printerServices.getEditOptions().then(resp => setEditOptions(resp));
        }
    }, [recordID, isOpen]);

    const loadRecord = (id) => {
        if (!id) return;
        setLoadingRecord(true);
        setCurrentRecord(null);
        printerServices.getPrinter(id, '?include=centre')
            .then(resp => setCurrentRecord(resp))
            .catch(() => {})
            .finally(() => setLoadingRecord(false));
    };

    const saveRecord = () => {
        if (!currentRecord) return;
        setSaving(true);
        const payload = { printer: _.pick(currentRecord, CAMPOS_GUARDABLES) };

        const after = (resp, isNew) => {
            (isNew ? onRecordAdd : onRecordChange)(resp);
            onClose();
            setSaving(false);
        };

        if (currentRecord.id) {
            printerServices.savePrinter(currentRecord.id, '?include=centre', payload)
                .then(resp => after(resp, false))
                .catch(() => setSaving(false));
        } else {
            printerServices.createPrinter('?include=centre', payload)
                .then(resp => after(resp, true))
                .catch(() => setSaving(false));
        }
    };

    const toggleActivation = () => {
        if (!currentRecord) return;
        if (!currentRecord.id) {
            setCurrentRecord({ ...currentRecord, active: !currentRecord.active });
            return;
        }
        setToggleActivationLoading(true);
        printerServices.togglePrinterActivation(currentRecord.id, '?include=centre')
            .then(resp => {
                setCurrentRecord(resp);
                onRecordChange(resp);
            })
            .catch(() => {})
            .finally(() => setToggleActivationLoading(false));
    };

    const modalTitle = () => {
        if (loadingRecord) return 'Cargando';
        if (currentRecord?.id) return currentRecord.name;
        return 'Nueva impresora';
    };

    return (
        <Dialog fullWidth maxWidth="sm" open={isOpen} onClose={onClose}>
            <DialogTitle>
                <Typography variant="h2" color="primary">{modalTitle()}</Typography>
                {currentRecord && (
                    <Chip
                        size="small"
                        color={currentRecord.active === false ? "default" : "success"}
                        label={currentRecord.active === false ? "Inactiva" : "Activa"}
                        sx={{ mt: 1 }}
                    />
                )}
            </DialogTitle>
            <DialogContent>
                {loadingRecord ? (
                    <Div sx={{ textAlign: 'center' }}>
                        <CircularProgress />
                    </Div>
                ) : currentRecord && (
                    <Grid container spacing={2} sx={{ pt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                autoFocus
                                fullWidth
                                size="small"
                                label="Nombre"
                                required
                                error={!currentRecord.name}
                                value={currentRecord.name || ''}
                                onChange={(e) => setCurrentRecord({ ...currentRecord, name: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                size="small"
                                options={editOptions?.centres || []}
                                getOptionLabel={(option) => option.code ? `${option.code} - ${option.name}` : `${option.name}`}
                                value={_.find(editOptions?.centres, ['id', currentRecord.centre_id]) || null}
                                onChange={(e, v) => setCurrentRecord({ ...currentRecord, centre_id: v?.id })}
                                renderInput={(params) => <TextField {...params} label="Centro *" sx={{ background: 'white' }} />}
                            />
                        </Grid>
                        <Grid item xs={8}>
                            <TextField
                                fullWidth
                                size="small"
                                label="IP"
                                placeholder="192.168.1.50"
                                helperText={!currentRecord.ip && !currentRecord.printer_name ? 'Indicá la IP o el nombre en el PC' : ' '}
                                error={!currentRecord.ip && !currentRecord.printer_name}
                                value={currentRecord.ip || ''}
                                onChange={(e) => setCurrentRecord({ ...currentRecord, ip: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth
                                size="small"
                                label="Puerto"
                                type="number"
                                value={currentRecord.port ?? 9100}
                                onChange={(e) => setCurrentRecord({ ...currentRecord, port: parseInt(e.target.value, 10) })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                size="small"
                                freeSolo
                                options={localPrinters || []}
                                value={currentRecord.printer_name || ''}
                                onChange={(e, v) => setCurrentRecord({ ...currentRecord, printer_name: v || '' })}
                                onInputChange={(e, v) => setCurrentRecord({ ...currentRecord, printer_name: v || '' })}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        label="Nombre en el PC (opcional)"
                                        helperText="Si la impresora está instalada en el PC, imprimimos por nombre y la IP deja de importar cuando el DHCP la cambia."
                                        sx={{ background: 'white' }}
                                    />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Div sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                <LoadingButton
                                    loading={buscando}
                                    size="small"
                                    variant="outlined"
                                    startIcon={<SearchOutlinedIcon />}
                                    onClick={buscarImpresoras}>
                                    Buscar en este PC
                                </LoadingButton>
                                <LoadingButton
                                    loading={probando}
                                    size="small"
                                    variant="outlined"
                                    startIcon={<NetworkCheckIcon />}
                                    disabled={!currentRecord.ip && !currentRecord.printer_name}
                                    onClick={probarConexion}>
                                    Probar conexión
                                </LoadingButton>
                            </Div>
                        </Grid>
                        {aviso && (
                            <Grid item xs={12}>
                                <Alert severity={aviso.tipo} onClose={() => setAviso(null)}>{aviso.texto}</Alert>
                            </Grid>
                        )}

                        <Grid item xs={12}><Divider sx={{ mt: 1 }} /></Grid>
                        <Grid item xs={12}>
                            <Typography variant="h5" sx={{ mb: 0 }}>Papel</Typography>
                            <Typography variant="body2" color="text.secondary">
                                Con esto se arma el ZPL: {Math.round((currentRecord.label_width_mm || 0) * (currentRecord.dpi || 203) / 25.4)} × {Math.round((currentRecord.label_height_mm || 0) * (currentRecord.dpi || 203) / 25.4)} dots.
                            </Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                size="small"
                                options={PAPELES}
                                getOptionLabel={(o) => o.label}
                                value={_.find(PAPELES, (p) => Number(p.ancho) === Number(currentRecord.label_width_mm)
                                    && Number(p.alto) === Number(currentRecord.label_height_mm)) || null}
                                onChange={(e, v) => v && setCurrentRecord({ ...currentRecord, label_width_mm: v.ancho, label_height_mm: v.alto })}
                                renderInput={(params) => <TextField {...params} label="Tamaño" sx={{ background: 'white' }} />}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth size="small" label="Ancho (mm)" type="number"
                                value={currentRecord.label_width_mm ?? 100}
                                onChange={(e) => setCurrentRecord({ ...currentRecord, label_width_mm: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                fullWidth size="small" label="Alto (mm)" type="number"
                                value={currentRecord.label_height_mm ?? 152.5}
                                onChange={(e) => setCurrentRecord({ ...currentRecord, label_height_mm: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <Autocomplete
                                size="small"
                                options={[203, 300, 600]}
                                getOptionLabel={(o) => `${o} dpi`}
                                value={currentRecord.dpi || 203}
                                onChange={(e, v) => setCurrentRecord({ ...currentRecord, dpi: v || 203 })}
                                renderInput={(params) => <TextField {...params} label="Resolución" sx={{ background: 'white' }} />}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Autocomplete
                                size="small"
                                options={['gap', 'continuo']}
                                getOptionLabel={(o) => o === 'gap' ? 'Etiqueta troquelada (gap)' : 'Rollo continuo'}
                                value={currentRecord.media_type || 'gap'}
                                onChange={(e, v) => setCurrentRecord({ ...currentRecord, media_type: v || 'gap' })}
                                renderInput={(params) => <TextField {...params} label="Medio" sx={{ background: 'white' }} />}
                            />
                        </Grid>
                        <Grid item xs={6}>
                            <Autocomplete
                                size="small"
                                options={editOptions?.kinds || ['zebra']}
                                value={currentRecord.kind || 'zebra'}
                                onChange={(e, v) => setCurrentRecord({ ...currentRecord, kind: v || 'zebra' })}
                                renderInput={(params) => <TextField {...params} label="Tipo" sx={{ background: 'white' }} />}
                            />
                        </Grid>
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={() => onClose()}>Cerrar</Button>
                <LoadingButton
                    loading={toggleActivationLoading}
                    variant="outlined"
                    color={currentRecord?.active === false ? "success" : "warning"}
                    size="small"
                    disabled={!currentRecord}
                    onClick={toggleActivation}>
                    {currentRecord?.active === false ? 'Activar' : 'Desactivar'}
                </LoadingButton>
                <LoadingButton
                    loading={saving}
                    variant="contained"
                    color="success"
                    size="small"
                    endIcon={<SaveOutlinedIcon />}
                    loadingPosition="end"
                    disabled={!currentRecord?.name || !currentRecord?.centre_id
                        || (!currentRecord?.ip && !currentRecord?.printer_name)}
                    onClick={saveRecord}>
                    {saving ? 'Guardando' : 'Guardar'}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
};

export default PrinterEditModal;
